/*
  Cart drawer.

  Everything it shows is Liquid: after any change the Cart API is asked for a
  fresh copy of sections/landing-cart-drawer.liquid and sections/cart-icon-bubble
  .liquid, and both are dropped straight in. No prices or translations are
  rebuilt here.

  window.LandingCart is the whole interface other scripts need:
    open()                  show the panel (and refresh it in the background)
    close()
    render(sections)        swap in sections returned by a cart request
    refresh()               pull the current contents from the server

  Nothing here is required for the cart to work: the icon keeps its href and the
  checkout button is a real form, so without JavaScript both fall back to the
  cart page.
*/
(() => {
  const drawer = () => document.querySelector('[data-cart-drawer]');
  const contents = () => document.getElementById('landing-cart-drawer-contents');

  let lastFocus = null;

  const sectionInnerHTML = (html) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const section = parsed.querySelector('.shopify-section');
    return section ? section.innerHTML : parsed.body.innerHTML;
  };

  const render = (sections) => {
    if (!sections) return;
    const panel = contents();
    if (panel && sections['landing-cart-drawer']) {
      panel.innerHTML = sectionInnerHTML(sections['landing-cart-drawer']);
    }
    const icon = document.getElementById('cart-icon-bubble');
    if (icon && sections['cart-icon-bubble']) {
      icon.innerHTML = sectionInnerHTML(sections['cart-icon-bubble']);
    }
  };

  const refresh = async () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', 'landing-cart-drawer,cart-icon-bubble');
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      render(await response.json());
    } catch (error) {
      // The panel keeps whatever it was last given.
    }
  };

  const open = () => {
    const element = drawer();
    if (!element || element.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    element.classList.add('is-open');
    // html.cart-open is what the header's own style block watches to pin the
    // bar, because a locked page has no scrollport for sticky.
    document.documentElement.classList.add('cart-open');
    // Everything else about holding the page still — overflow, the wheel and
    // Lenis — is the shared lock's job. The panel stays scrollable.
    window.ScrollLock?.lock(element.querySelector('[data-cart-panel]'));
    element.querySelector('[data-cart-panel]')?.focus();
    refresh();
  };

  const close = () => {
    const element = drawer();
    if (!element || !element.classList.contains('is-open')) return;
    element.classList.remove('is-open');
    document.documentElement.classList.remove('cart-open');
    window.ScrollLock?.unlock();
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
    lastFocus = null;
  };

  const changeQuantity = async (item, quantity) => {
    if (item.getAttribute('aria-busy') === 'true') return;
    item.setAttribute('aria-busy', 'true');
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root.replace(/\/$/, '')}/cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(item.dataset.line),
          quantity,
          sections: 'landing-cart-drawer,cart-icon-bubble',
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message);
      render(result.sections);
    } catch (error) {
      // Nothing partial is left on screen: ask the server what the cart is now.
      item.removeAttribute('aria-busy');
      refresh();
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener) {
      event.preventDefault();
      open();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (quantityButton) {
      const item = quantityButton.closest('[data-cart-item]');
      if (item) changeQuantity(item, Number(quantityButton.dataset.cartQuantity));
    }
  });

  // Checkout: the button keeps its spinner, because the page is either leaving
  // or the ticket app is about to open its own modal over it. The panel closes
  // either way — the page lock would stop that modal from scrolling.
  //
  // Watched on click rather than submit: the ticket app cancels the submit to
  // collect attendee details first, so the submit event never reaches here.
  const startCheckout = (button) => {
    button.setAttribute('aria-busy', 'true');
    close();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-checkout] .btn');
    if (button) startCheckout(button);
  });

  document.addEventListener('submit', (event) => {
    const button = event.target.closest('[data-cart-checkout]')?.querySelector('.btn');
    if (button) startCheckout(button);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  window.LandingCart = { open, close, render, refresh };
})();
